import { useCallback } from 'react';
import { useQuery } from 'react-apollo';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import {
	useSetOnboardingState,
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { isErrorMarkedAsHandled, markErrorAsHandled } from '@confluence/graphql';
import { expValEqualsNoExposure } from '@confluence/feature-experiments';
import { ConfluenceEdition } from '@confluence/session-data';

import type { LicenseDetailsQuery as LicenseDetailsQueryType } from './__types__/LicenseDetailsQuery';
import { LicenseDetailsQuery } from './graphql/LicenceDetailsQuery.graphql';
import { getTrialDaysLeft } from './getTrialDaysLeftFromQuery';

export const EDITION_AWARENESS_STANDARD_TO_PREMIUM_EXPERIMENT_PILL_DISMISSED =
	'growth-luna-edition-awareness-standard-to-premium:treatmentPillDismissed';

export const useConfluenceStandardToPremiumPillExperiment = ({
	edition,
	isSiteAdmin,
	shouldSkipLicenseQuery,
}: {
	edition?: ConfluenceEdition | null;
	isSiteAdmin?: boolean;
	shouldSkipLicenseQuery: boolean;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();
	const enrolled =
		expValEqualsNoExposure(
			'edition_awareness_standard_upsell_confluence_ab',
			'cohort',
			'treatment',
		) ||
		expValEqualsNoExposure('edition_awareness_standard_upsell_confluence_ab', 'cohort', 'control');

	// Dismiss logic
	// fetch dismiss state for EA pill
	const { data, error, loading } = useGetOnboardingState(
		EDITION_AWARENESS_STANDARD_TO_PREMIUM_EXPERIMENT_PILL_DISMISSED,
		!enrolled,
	);

	if (error && !isErrorMarkedAsHandled(error)) {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'failed',
				actionSubject: 'onboarding-state',
				source: 'useConfluenceStandardToPremiumPillExperiment',
				attributes: {
					growthFeature: 'EditionAwareness',
					error: error.message,
				},
			},
		}).fire();
		markErrorAsHandled(error);
	}

	const { treatmentPillDismissed } = deserializeState(data);
	const isDismissed = loading || !!error || !!treatmentPillDismissed;

	// set dismiss state for EA pill
	const handleDismiss = useCallback(() => {
		if (enrolled) {
			setOnboardingState({
				key: EDITION_AWARENESS_STANDARD_TO_PREMIUM_EXPERIMENT_PILL_DISMISSED,
				value: 'true',
			});
		}
	}, [setOnboardingState, enrolled]);

	// Trial State logic
	// get trial end timestamp from tenant context
	const {
		data: licenseDetailsData,
		loading: isLicenseLoading,
		error: trialDetailsError,
	} = useQuery<LicenseDetailsQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		LicenseDetailsQuery,
		{
			skip: !enrolled || shouldSkipLicenseQuery,
		},
	);

	const trialEndDate = licenseDetailsData?.license?.trialEndDate;
	const trialDaysLeft = getTrialDaysLeft(trialEndDate);
	const isInTrial =
		!licenseDetailsData || isLicenseLoading || trialDetailsError
			? undefined
			: trialDaysLeft
				? trialDaysLeft > 0
				: false;

	if (trialDetailsError && !isErrorMarkedAsHandled(trialDetailsError)) {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'failed',
				actionSubject: 'trial-details',
				source: 'useConfluenceStandardToPremiumPillExperiment',
				attributes: {
					growthFeature: 'EditionAwareness',
					error: trialDetailsError.message,
				},
			},
		}).fire();
		markErrorAsHandled(trialDetailsError);
	}

	// Check if the user is eligible for the custom EA upsell component
	const isEligibleForUpsellEa =
		edition === ConfluenceEdition.STANDARD &&
		enrolled &&
		isInTrial === false &&
		isSiteAdmin &&
		fg('edition_awareness_upsell_custom_ea_implementation');

	return {
		isEligibleForUpsellEa,
		isDismissed,
		handleDismiss,
	};
};
